import { FC, useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  CardActions,
  TextField,
} from '@mui/material'
import { Add as AddIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material'
import { useGetInventoryItemsQuery } from '../redux/hooks.ts'
import { InventoryItemModel } from '@itemize/server/models'
import { InventorySearchSortControls } from './inventory-search-sort-controls.tsx'
import { useInventorySearchAndSort } from '../hooks/use-inventory-search-and-sort.ts'

interface AddInventoryItemsModalProps {
  open: boolean
  onClose: () => void
  onAddItems: (items: Array<{ inventoryItemId: string; quantity: number }>) => void
  existingItemIds?: string[]
}

const CONFIG = {
  GRID_MIN_WIDTH: 200,
  GRID_GAP: 2,
}

export const AddInventoryItemsModal: FC<AddInventoryItemsModalProps> = ({
  open,
  onClose,
  onAddItems,
  existingItemIds = [],
}) => {
  const { data: items, isLoading, error } = useGetInventoryItemsQuery()
  const [selectedItems, setSelectedItems] = useState<Map<string, number>>(new Map())

  const {
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    toggleSortOrder,
    filteredAndSortedItems,
    resetSearch,
  } = useInventorySearchAndSort({
    items,
    defaultSortBy: 'updatedAt',
    defaultSortOrder: 'desc',
  })

  // Reset search query when modal opens
  useEffect(() => {
    if (open) {
      resetSearch()
      setSelectedItems(new Map())
    }
  }, [open, resetSearch])

  const handleToggleItem = (itemId: string) => {
    const newSelected = new Map(selectedItems)
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId)
    } else {
      newSelected.set(itemId, 1)
    }
    setSelectedItems(newSelected)
  }

  const handleQuantityChange = (itemId: string, quantity: number) => {
    const newSelected = new Map(selectedItems)
    if (quantity > 0 && Number.isInteger(quantity)) {
      newSelected.set(itemId, quantity)
    } else if (quantity <= 0) {
      newSelected.delete(itemId)
    }
    setSelectedItems(newSelected)
  }

  const handleAddItems = () => {
    const itemsToAdd = Array.from(selectedItems.entries()).map(([inventoryItemId, quantity]) => ({
      inventoryItemId,
      quantity,
    }))
    onAddItems(itemsToAdd)
    setSelectedItems(new Map())
    onClose()
  }

  const handleCancel = () => {
    setSelectedItems(new Map())
    onClose()
  }

  const isItemInProject = (itemId: string) => existingItemIds.includes(itemId)
  const isItemSelected = (itemId: string) => selectedItems.has(itemId)
  const getItemQuantity = (itemId: string) => selectedItems.get(itemId) ?? 1

  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    )
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Alert severity="error">Error loading items: {String(error)}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Add Inventory Items</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', padding: 0 }}>
        {/* Search and Sort Controls */}
        <Box
          sx={{
            padding: 3,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <InventorySearchSortControls
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            sortOrder={sortOrder}
            onToggleSortOrder={toggleSortOrder}
          />
        </Box>

        {/* Items Grid */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', padding: 3 }}>
          {filteredAndSortedItems.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="300px"
              textAlign="center"
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No items found
              </Typography>
              <Typography color="text.secondary">
                {searchQuery.trim()
                  ? 'Try adjusting your search terms'
                  : 'No inventory items available'}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fit, minmax(${CONFIG.GRID_MIN_WIDTH}px, 1fr))`,
                gap: CONFIG.GRID_GAP,
              }}
            >
              {filteredAndSortedItems.map((item) => (
                <InventoryItemCard
                  key={item.id}
                  item={item}
                  isInProject={isItemInProject(item.id)}
                  isSelected={isItemSelected(item.id)}
                  quantity={getItemQuantity(item.id)}
                  onToggle={() => handleToggleItem(item.id)}
                  onQuantityChange={(quantity) => handleQuantityChange(item.id, quantity)}
                />
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ padding: 3, justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
        </Typography>
        <Box display="flex" gap={1}>
          <Button onClick={handleCancel} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleAddItems} variant="contained" disabled={selectedItems.size === 0}>
            Add {selectedItems.size > 0 ? `(${selectedItems.size})` : ''}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

interface InventoryItemCardProps {
  item: InventoryItemModel
  isInProject: boolean
  isSelected: boolean
  quantity: number
  onToggle: () => void
  onQuantityChange: (quantity: number) => void
}

const InventoryItemCard: FC<InventoryItemCardProps> = ({
  item,
  isInProject,
  isSelected,
  quantity,
  onToggle,
  onQuantityChange,
}) => {
  const getActionButton = () => {
    if (isInProject) {
      return (
        <IconButton
          size="small"
          sx={{
            backgroundColor: 'success.main',
            color: 'success.contrastText',
            '&:hover': {
              backgroundColor: 'success.dark',
            },
          }}
          disabled
        >
          <CheckIcon />
        </IconButton>
      )
    }

    if (isSelected) {
      return (
        <IconButton
          size="small"
          onClick={onToggle}
          sx={{
            backgroundColor: 'success.main',
            color: 'success.contrastText',
            '&:hover': {
              backgroundColor: 'success.dark',
            },
          }}
        >
          <CheckIcon />
        </IconButton>
      )
    }

    return (
      <IconButton
        size="small"
        onClick={onToggle}
        sx={{
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
        }}
      >
        <AddIcon />
      </IconButton>
    )
  }

  return (
    <Card
      elevation={2}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
        opacity: isInProject ? 0.6 : 1,
      }}
    >
      <CardMedia
        component="img"
        height="200"
        sx={{
          padding: '1em 1em 0 1em',
          objectFit: 'contain',
          borderRadius: '1em',
        }}
        image={item.imageUrl || '/images/image-placeholder.jpg'}
        alt={item.name}
      />
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" component="h3" gutterBottom fontSize={16}>
          {item.name}
        </Typography>

        {item.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              marginBottom: 2,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {item.description}
          </Typography>
        )}
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', flexGrow: 1, alignContent: 'flex-end' }}
        >
          <strong>${(item.priceCents / 100).toFixed(2)}</strong>
        </Typography>
      </CardContent>
      <CardActions sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {getActionButton()}
        {isSelected && !isInProject && (
          <TextField
            type="number"
            size="small"
            value={quantity}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10)
              if (!isNaN(value)) {
                onQuantityChange(value)
              }
            }}
            slotProps={{
              htmlInput: {
                min: 1,
                step: 1,
                style: { width: '80px', textAlign: 'center' },
              },
            }}
            sx={{ marginLeft: 'auto' }}
          />
        )}
      </CardActions>
    </Card>
  )
}
